const DEFAULT_PADDING = 8;
const TOOLTIP_WIDTH = 480;
const TOOLTIP_HEIGHT = 320;
const GAP = 8;
const VIEWPORT_MARGIN = 16;
const COMPACT_TOOLTIP_WIDTH = 360;
const COMPACT_TOOLTIP_BREAKPOINT = 768;
const LOW_TOOLTIP_TOP_RATIO = 0.42;

export function findTutorialTarget(targetId, options = {}) {
  if (!targetId || typeof document === "undefined") return null;
  const root = options.root || document;
  const targetIds = [
    targetId,
    ...(Array.isArray(options.fallbackTargetIds) ? options.fallbackTargetIds : []),
  ].filter(Boolean);
  for (const id of targetIds) {
    const selector = `[data-tutorial-id="${escapeAttributeValue(id)}"]`;
    const target = root.querySelector(selector);
    if (target) return target;
  }
  if (options.warn) {
    // eslint-disable-next-line no-console
    console.warn(`[Tutorial] Target not found: ${targetId} — step skipped.`);
  }
  return null;
}

// -----------------------------------------------------------------------
// Responsive tutorial contract — applies to every tutorial in the system
// -----------------------------------------------------------------------
// Tutorials must work across Desktop, Tablet, and Mobile. On compact
// viewports the tooth chart only renders one arch side at a time (Right
// or Left toggle), so a step targeting a tooth on the hidden side can't
// find its anchor.
//
// The contract:
//   1. Tooth-targeted steps use the target id format
//        "charting-tooth-{FDI}"  or  "charting-tooth-{FDI}-surface-{x}"
//      so the engine can read the arch side from the id alone.
//   2. The arch-side toggle exposes
//        data-tutorial-id="charting-side-right"  /  "charting-side-left"
//      so the engine can click it like a user would.
//   3. TutorialProvider's measure() calls getToothSideFromTargetId() and
//      auto-clicks the matching toggle once before falling back to the
//      retry/skip loop. Batch steps with completionTargetIds (e.g. Today's
//      composite findings) also auto-flip the arch side as soon as the
//      visible-side targets are exhausted, so the user can keep clicking
//      without stopping to switch arches manually.
//
// New tutorials that target the tooth chart get this behavior for free —
// don't add bespoke side-switching per tutorial. New responsive UI states
// that can hide a tutorial target (palette sheets, collapsible drawers)
// should extend this same engine pattern: expose a tutorial id on the
// state-toggling control, then teach the engine to recognize when a step
// is gated behind it.

// Map an FDI tooth number to the arch side it sits on.
// Quadrants 1, 4, 5, 8 are the patient's right; 2, 3, 6, 7 are the left.
export function getToothSideFromFdi(fdi) {
  const n = Number(fdi);
  if (!Number.isFinite(n)) return null;
  const quadrant = Math.floor(n / 10);
  if ([1, 4, 5, 8].includes(quadrant)) return "right";
  if ([2, 3, 6, 7].includes(quadrant)) return "left";
  return null;
}

// Extract the arch side from a tutorial target id that names a tooth,
// e.g. "charting-tooth-15-surface-o" or "charting-tooth-46". Returns
// null for non-tooth targets so the auto-switch logic can no-op.
export function getToothSideFromTargetId(targetId) {
  if (!targetId) return null;
  const match = /^charting-tooth-(\d+)/.exec(String(targetId));
  if (!match) return null;
  return getToothSideFromFdi(match[1]);
}

export function calculateSpotlightRect(element, padding = DEFAULT_PADDING, options = {}) {
  const rect = getElementViewportRect(element);
  const shape = getSpotlightShape(element, options.shape);
  const zoom = shape === "rect" ? getSpotlightZoom(element, options.zoom) : 1;
  const paddedWidth = (rect.width + padding * 2) * zoom;
  const paddedHeight = (rect.height + padding * 2) * zoom;
  let width = Math.max(paddedWidth, options.minWidth || 0);
  let height = Math.max(paddedHeight, options.minHeight || 0);
  if (shape === "circle") {
    const diameter = Math.max(width, height);
    width = diameter;
    height = diameter;
  }
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  const top = Math.max(0, centerY - height / 2);
  const left = Math.max(0, centerX - width / 2);
  const spotlightRect = {
    top,
    left,
    width,
    height,
    right: left + width,
    bottom: top + height,
  };
  if (shape !== "rect") {
    spotlightRect.shape = shape;
  }
  if (zoom !== 1) {
    spotlightRect.zoom = zoom;
  }
  return spotlightRect;
}

function getSpotlightShape(element, optionShape) {
  const shape = optionShape || element?.dataset?.tutorialSpotlightShape || "rect";
  return ["rect", "pill", "circle"].includes(shape) ? shape : "rect";
}

function getSpotlightZoom(element, optionZoom) {
  const rawZoom = optionZoom ?? element?.dataset?.tutorialSpotlightZoom;
  const zoom = Number(rawZoom);
  return Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
}

function getElementViewportRect(element) {
  if (
    element
    && typeof element.getBBox === "function"
    && typeof element.getScreenCTM === "function"
  ) {
    try {
      const box = element.getBBox();
      const matrix = element.getScreenCTM();
      if (box && matrix && Number(box.width) > 0 && Number(box.height) > 0) {
        const corners = [
          transformSvgPoint(matrix, box.x, box.y),
          transformSvgPoint(matrix, box.x + box.width, box.y),
          transformSvgPoint(matrix, box.x, box.y + box.height),
          transformSvgPoint(matrix, box.x + box.width, box.y + box.height),
        ];
        const xs = corners.map((point) => point.x);
        const ys = corners.map((point) => point.y);
        const left = Math.min(...xs);
        const top = Math.min(...ys);
        const right = Math.max(...xs);
        const bottom = Math.max(...ys);
        return {
          top,
          left,
          width: right - left,
          height: bottom - top,
          right,
          bottom,
        };
      }
    } catch {
      // Fall through to normal DOM measurement. Some SVG nodes cannot
      // provide bbox data until fully rendered.
    }
  }
  return element.getBoundingClientRect();
}

function transformSvgPoint(matrix, x, y) {
  return {
    x: matrix.a * x + matrix.c * y + matrix.e,
    y: matrix.b * x + matrix.d * y + matrix.f,
  };
}

export function resolveTooltipPosition(rect, requested = "auto", viewport = viewportSize()) {
  if (requested && requested !== "auto") return requested;
  const spaces = {
    bottom: viewport.height - rect.bottom,
    top: rect.top,
    right: viewport.width - rect.right,
    left: rect.left,
  };
  if (spaces.bottom >= TOOLTIP_HEIGHT + GAP) return "bottom";
  if (spaces.top >= TOOLTIP_HEIGHT + GAP) return "top";
  if (spaces.right >= TOOLTIP_WIDTH + GAP) return "right";
  if (spaces.left >= TOOLTIP_WIDTH + GAP) return "left";
  return Object.entries(spaces).sort((a, b) => b[1] - a[1])[0][0];
}

export function calculateTooltipStyle(rect, requested = "auto", viewport = viewportSize(), options = {}) {
  const maxTooltipWidth = viewport.width < COMPACT_TOOLTIP_BREAKPOINT
    ? COMPACT_TOOLTIP_WIDTH
    : TOOLTIP_WIDTH;
  const width = Math.min(maxTooltipWidth, Math.max(280, viewport.width - VIEWPORT_MARGIN * 2));
  const height = Math.min(TOOLTIP_HEIGHT, Math.max(120, viewport.height - VIEWPORT_MARGIN * 2));
  if (requested === "center") {
    return {
      side: "center",
      placement: "center",
      width,
      height,
      top: clamp((viewport.height - height) / 2, VIEWPORT_MARGIN, Math.max(VIEWPORT_MARGIN, viewport.height - height - VIEWPORT_MARGIN)),
      left: clamp((viewport.width - width) / 2, VIEWPORT_MARGIN, Math.max(VIEWPORT_MARGIN, viewport.width - width - VIEWPORT_MARGIN)),
    };
  }
  const avoidRects = normalizeAvoidRects(options.avoidRects);
  const cornerCandidates = getTooltipCornerCandidates(rect, width, height, requested, viewport);
  const candidates = avoidRects.length > 0
    ? getTooltipAvoidanceCandidates(cornerCandidates, avoidRects, width, height, viewport)
    : cornerCandidates;
  const selected = avoidRects.length > 0
    ? getLowestCollisionCandidate(candidates, width, height, viewport, avoidRects)
    : candidates.find((candidate) => isTooltipCandidateVisible(candidate, width, height, viewport))
      || getLowestOverflowCandidate(candidates, width, height, viewport);
  const maxLeft = Math.max(VIEWPORT_MARGIN, viewport.width - width - VIEWPORT_MARGIN);
  const maxTop = Math.max(VIEWPORT_MARGIN, viewport.height - height - VIEWPORT_MARGIN);

  return {
    side: selected.placement.split("-")[0],
    placement: selected.placement,
    width,
    height,
    top: clamp(selected.top, VIEWPORT_MARGIN, maxTop),
    left: clamp(selected.left, VIEWPORT_MARGIN, maxLeft),
  };
}

function getTooltipCornerCandidates(rect, width, height, requested, viewport = viewportSize()) {
  const topCandidates = [
    { placement: "top-right", top: rect.top - height - GAP, left: rect.right + GAP },
    { placement: "top-left", top: rect.top - height - GAP, left: rect.left - GAP - width },
  ];
  const bottomCandidates = [
    { placement: "bottom-right", top: rect.bottom + GAP, left: rect.right + GAP },
    { placement: "bottom-left", top: rect.bottom + GAP, left: rect.left - GAP - width },
  ];
  const shouldPreferTop = (
    (!requested || requested === "auto")
    && rect.top >= height + GAP + VIEWPORT_MARGIN
    && rect.bottom + GAP > viewport.height * LOW_TOOLTIP_TOP_RATIO
  );
  const candidates = shouldPreferTop
    ? [...topCandidates, ...bottomCandidates]
    : [...bottomCandidates, ...topCandidates];

  if (!requested || requested === "auto") return candidates;
  return candidates.filter((candidate) => candidate.placement.startsWith(requested));
}

function getTooltipAvoidanceCandidates(cornerCandidates, avoidRects, width, height, viewport) {
  const avoidBounds = getBoundingRect(avoidRects);
  return uniqueCandidates([
    ...cornerCandidates,
    ...(avoidBounds ? getAvoidanceLaneCandidates(avoidBounds, width, height) : []),
    ...getViewportFallbackCandidates(width, height, viewport),
  ]);
}

function getAvoidanceLaneCandidates(rect, width, height) {
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  return [
    { placement: "avoid-right", top: centerY - height / 2, left: rect.right + GAP },
    { placement: "avoid-left", top: centerY - height / 2, left: rect.left - width - GAP },
    { placement: "avoid-bottom", top: rect.bottom + GAP, left: centerX - width / 2 },
    { placement: "avoid-top", top: rect.top - height - GAP, left: centerX - width / 2 },
  ];
}

function getViewportFallbackCandidates(width, height, viewport) {
  const maxLeft = Math.max(VIEWPORT_MARGIN, viewport.width - width - VIEWPORT_MARGIN);
  const maxTop = Math.max(VIEWPORT_MARGIN, viewport.height - height - VIEWPORT_MARGIN);
  const centerLeft = clamp((viewport.width - width) / 2, VIEWPORT_MARGIN, maxLeft);
  const centerTop = clamp((viewport.height - height) / 2, VIEWPORT_MARGIN, maxTop);
  return [
    { placement: "viewport-top-left", top: VIEWPORT_MARGIN, left: VIEWPORT_MARGIN },
    { placement: "viewport-top-center", top: VIEWPORT_MARGIN, left: centerLeft },
    { placement: "viewport-top-right", top: VIEWPORT_MARGIN, left: maxLeft },
    { placement: "viewport-middle-left", top: centerTop, left: VIEWPORT_MARGIN },
    { placement: "viewport-middle-right", top: centerTop, left: maxLeft },
    { placement: "viewport-bottom-left", top: maxTop, left: VIEWPORT_MARGIN },
    { placement: "viewport-bottom-center", top: maxTop, left: centerLeft },
    { placement: "viewport-bottom-right", top: maxTop, left: maxLeft },
  ];
}

function isTooltipCandidateVisible(candidate, width, height, viewport) {
  return (
    candidate.left >= VIEWPORT_MARGIN
    && candidate.top >= VIEWPORT_MARGIN
    && candidate.left + width <= viewport.width - VIEWPORT_MARGIN
    && candidate.top + height <= viewport.height - VIEWPORT_MARGIN
  );
}

function getLowestOverflowCandidate(candidates, width, height, viewport) {
  return candidates
    .map((candidate) => ({
      ...candidate,
      overflow: calculateOverflow(candidate, width, height, viewport),
    }))
    .sort((a, b) => a.overflow - b.overflow)[0];
}

function getLowestCollisionCandidate(candidates, width, height, viewport, avoidRects) {
  return candidates
    .map((candidate, index) => {
      const clamped = clampTooltipCandidate(candidate, width, height, viewport);
      return {
        ...clamped,
        overlap: calculateAvoidanceOverlap(clamped, width, height, avoidRects),
        overflow: calculateOverflow(candidate, width, height, viewport),
        index,
      };
    })
    .sort((a, b) => a.overlap - b.overlap || a.overflow - b.overflow || a.index - b.index)[0];
}

function calculateOverflow(candidate, width, height, viewport) {
  return (
    Math.max(0, VIEWPORT_MARGIN - candidate.left)
    + Math.max(0, VIEWPORT_MARGIN - candidate.top)
    + Math.max(0, candidate.left + width - (viewport.width - VIEWPORT_MARGIN))
    + Math.max(0, candidate.top + height - (viewport.height - VIEWPORT_MARGIN))
  );
}

function calculateAvoidanceOverlap(candidate, width, height, avoidRects) {
  const tooltipRect = {
    top: candidate.top,
    left: candidate.left,
    right: candidate.left + width,
    bottom: candidate.top + height,
  };
  return avoidRects.reduce((total, rect) => total + getOverlapArea(tooltipRect, rect), 0);
}

function getOverlapArea(a, b) {
  const width = Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left));
  const height = Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
  return width * height;
}

function clampTooltipCandidate(candidate, width, height, viewport) {
  const maxLeft = Math.max(VIEWPORT_MARGIN, viewport.width - width - VIEWPORT_MARGIN);
  const maxTop = Math.max(VIEWPORT_MARGIN, viewport.height - height - VIEWPORT_MARGIN);
  return {
    ...candidate,
    top: clamp(candidate.top, VIEWPORT_MARGIN, maxTop),
    left: clamp(candidate.left, VIEWPORT_MARGIN, maxLeft),
  };
}

function getBoundingRect(rects) {
  if (!Array.isArray(rects) || rects.length === 0) return null;
  const left = Math.min(...rects.map((rect) => rect.left));
  const top = Math.min(...rects.map((rect) => rect.top));
  const right = Math.max(...rects.map((rect) => rect.right));
  const bottom = Math.max(...rects.map((rect) => rect.bottom));
  return {
    top,
    left,
    right,
    bottom,
    width: right - left,
    height: bottom - top,
  };
}

function normalizeAvoidRects(rects) {
  if (!Array.isArray(rects)) return [];
  return rects
    .map((rect) => {
      if (!rect) return null;
      const top = Number(rect.top);
      const left = Number(rect.left);
      const right = Number(rect.right ?? left + Number(rect.width));
      const bottom = Number(rect.bottom ?? top + Number(rect.height));
      if (![top, left, right, bottom].every(Number.isFinite)) return null;
      if (right <= left || bottom <= top) return null;
      return {
        top,
        left,
        right,
        bottom,
        width: right - left,
        height: bottom - top,
      };
    })
    .filter(Boolean);
}

function uniqueCandidates(candidates) {
  const seen = new Set();
  return candidates.filter((candidate) => {
    const key = [
      candidate.placement,
      Math.round(candidate.top),
      Math.round(candidate.left),
    ].join(":");
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function clamp(value, min, max) {
  return Math.min(Math.max(min, value), max);
}

export function getNextAvailableStepIndex(steps, currentIndex, direction) {
  const nextIndex = currentIndex + direction;
  if (nextIndex < 0 || nextIndex >= steps.length) return null;
  const target = findTutorialTarget(steps[nextIndex]?.targetId);
  if (target) return nextIndex;
  return getNextAvailableStepIndex(steps, nextIndex, direction);
}

export function getTutorialInteractiveTargetIds(step) {
  if (!step) return [];
  if (Array.isArray(step.triggerTargetIds) && step.triggerTargetIds.length > 0) return step.triggerTargetIds;
  if (typeof step.triggerEvent === "string" && step.triggerEvent.startsWith("tutorial:") && step.targetId) return [step.targetId];
  if (step.confirmation && step.targetId) return [step.targetId];
  return [];
}

export function eventMatchesTutorialTarget(event, targetIds = []) {
  return Boolean(getMatchedTutorialTargetId(event, targetIds));
}

export function getMatchedTutorialTargetId(event, targetIds = []) {
  if (!event || !Array.isArray(targetIds) || targetIds.length === 0) return null;
  const path = typeof event.composedPath === "function" ? event.composedPath() : [];
  const nodes = path.length > 0 ? path : [event.target];
  for (const node of nodes) {
    if (!node || node.nodeType !== 1) continue;
    for (const targetId of targetIds) {
      const selector = `[data-tutorial-id="${escapeAttributeValue(targetId)}"]`;
      if (node.matches?.(selector) || node.closest?.(selector)) return targetId;
    }
  }
  return null;
}

export function routeMatches(currentPathname, currentSearch, route) {
  const [path, search = ""] = String(route || "").split("?");
  const normalizedSearch = search ? `?${search}` : "";
  if (currentPathname !== path) return false;
  if (!normalizedSearch) return true;
  return currentSearch === normalizedSearch;
}

function viewportSize() {
  if (typeof window === "undefined") return { width: 1280, height: 720 };
  return {
    width: window.innerWidth || 1280,
    height: window.innerHeight || 720,
  };
}

function escapeAttributeValue(value) {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") return CSS.escape(value);
  return String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}
